// Teacher Directory Helper Functions
// This file contains helper functions for processing and formatting teacher data

// Get teacher's subjects from the available fields
export const getTeacherSubjects = (teacher) => {
  const subjects = [];
  
  // Check for subject_specialty first
  if (teacher.subject_specialty) {
    subjects.push(teacher.subject_specialty);
  }
  
  // Check for taught_subj1 to taught_subj6 fields (if they exist)
  for (let i = 1; i <= 6; i++) {
    const subject = teacher[`taught_subj${i}`];
    if (subject) {
      subjects.push(subject);
    }
  }
  
  // If no subjects found, use department as fallback
  if (subjects.length === 0 && teacher.department) {
    subjects.push(teacher.department);
  }
  
  return subjects;
};

// Get teacher's ECA responsibilities from available fields
export const getTeacherECAs = (teacher) => {
  const ecas = [];
  
  // Check for eca1 to eca5 fields (if they exist)
  for (let i = 1; i <= 5; i++) {
    const eca = teacher[`eca${i}`];
    if (eca) {
      ecas.push(eca);
    }
  }
  
  // If no ECAs found, return empty array
  return ecas;
};

// Get teacher's committee memberships from available fields
export const getTeacherCommittees = (teacher) => {
  const committees = [];
  
  // Check for member1 to member6 fields (if they exist)
  for (let i = 1; i <= 6; i++) {
    const committee = teacher[`member${i}`];
    if (committee) {
      committees.push(committee);
    }
  }
  
  // If no committees found, return empty array
  return committees;
};

// Get teacher's duties from available fields
export const getTeacherDuties = (teacher) => {
  const duties = [];
  
  // Check for duty1 to duty6 fields (if they exist)
  for (let i = 1; i <= 6; i++) {
    const duty = teacher[`duty${i}`];
    if (duty) {
      duties.push(duty);
    }
  }
  
  // If no duties found, return empty array
  return duties;
};

// Get teacher's class assignments (class master or vice class master)
export const getTeacherClassAssignments = (teacher, classes) => {
  if (!teacher || !classes || !classes.length) return [];
  
  return classes.filter(cls => {
    // Check if teacher is class master or vice class master
    return cls.class_master === teacher.teacher_id || cls.class_master === teacher.id ||
           cls.vice_class_master === teacher.teacher_id || cls.vice_class_master === teacher.id;
  });
};

// Get unique departments from teachers data
export const getDepartments = (teachers) => {
  const departments = new Set();
  teachers.forEach(teacher => {
    if (teacher.department) {
      departments.add(teacher.department);
    }
  });
  return ['All', ...Array.from(departments).sort()];
};

// Filter teachers based on search term and department
export const filterTeachers = (teachers, searchTerm, departmentFilter) => {
  return teachers.filter(teacher => {
    const matchesSearch = 
      (teacher.name && teacher.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (teacher.english_name && teacher.english_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (teacher.department && teacher.department.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesDepartment = departmentFilter === 'All' || 
      (teacher.department && teacher.department === departmentFilter);
    
    return matchesSearch && matchesDepartment;
  });
};

// Group teachers by subjects for the Subject Teachers view
export const groupTeachersBySubjects = (teachers) => {
  const subjectTeacherMap = {};
  teachers.forEach(teacher => {
    const subjects = getTeacherSubjects(teacher);
    subjects.forEach(subject => {
      if (!subjectTeacherMap[subject]) {
        subjectTeacherMap[subject] = [];
      }
      subjectTeacherMap[subject].push(teacher);
    });
  });
  return subjectTeacherMap;
};

// Format teacher display name
export const getTeacherDisplayName = (teacher) => {
  if (teacher.name) return teacher.name;
  if (teacher.english_name) return teacher.english_name;
  return '未命名教師';
};

// Format teacher title/role
export const getTeacherTitle = (teacher) => {
  if (teacher.title) return teacher.title;
  if (teacher.position) return teacher.position;
  if (teacher.department) return teacher.department;
  return '教師';
};

// Get teacher initials for avatar display
export const getTeacherInitials = (teacher) => {
  if (teacher.name) return teacher.name.substring(0, 1);
  if (teacher.english_name) return teacher.english_name.substring(0, 1);
  return 'T';
};

// Check if teacher has any responsibilities
export const hasTeacherResponsibilities = (teacher) => {
  const subjects = getTeacherSubjects(teacher);
  const ecas = getTeacherECAs(teacher);
  const committees = getTeacherCommittees(teacher);
  const duties = getTeacherDuties(teacher);
  
  return subjects.length > 0 || ecas.length > 0 || committees.length > 0 || duties.length > 0;
};

// Get teacher's role in a specific class
export const getTeacherClassRole = (teacher, classData) => {
  if (classData.class_master === teacher.teacher_id || classData.class_master === teacher.id) {
    return '班主任';
  } else if (classData.vice_class_master === teacher.teacher_id || classData.vice_class_master === teacher.id) {
    return '副班主任';
  }
  return null;
};
